import React, { createContext, useContext, useReducer, ReactNode } from 'react';

interface LevelAResponses {
  [key: string]: boolean | number;
}

interface ModuleResponses {
  [key: string]: boolean | number;
}

export interface AssessmentState {
  levelA: LevelAResponses;
  modules_selected: string[];
  modules: { [key: string]: ModuleResponses };
  currentStep: 'welcome' | 'levelA' | 'modules' | 'questions' | 'results';
  assessment_id?: string;
  created_at?: string;
}

export type AssessmentAction =
  | { type: 'SET_LEVEL_A_ANSWERS'; payload: { [key: string]: boolean } }
  | { type: 'SET_MODULES_SELECTED'; payload: string[] }
  | { type: 'SET_MODULE_ANSWERS'; payload: { module: string; answers: { [key: string]: boolean } } }
  | { type: 'SET_CURRENT_STEP'; payload: AssessmentState['currentStep'] }
  | { type: 'RESET_ASSESSMENT' }
  | { type: 'INITIALIZE_ASSESSMENT' };

const initialState: AssessmentState = {
  levelA: { yes_count: 0 } as LevelAResponses,
  modules_selected: [],
  modules: {},
  currentStep: 'welcome',
};

function assessmentReducer(state: AssessmentState, action: AssessmentAction): AssessmentState {
  switch (action.type) {
    case 'SET_LEVEL_A_ANSWERS':
      const yes_count = Object.values(action.payload).filter(Boolean).length;
      return {
        ...state,
        levelA: { ...action.payload, yes_count },
        currentStep: 'modules',
      };

    case 'SET_MODULES_SELECTED':
      return {
        ...state,
        modules_selected: action.payload,
        currentStep: action.payload.length > 0 ? 'questions' : 'results',
      };

    case 'SET_MODULE_ANSWERS':
      const { module, answers } = action.payload;
      const module_yes_count = Object.values(answers).filter(Boolean).length;
      const updatedModules = {
        ...state.modules,
        [module]: { ...answers, yes_count: module_yes_count },
      };
      
      // Check if all selected modules have been answered
      const allModulesAnswered = state.modules_selected.every(mod => updatedModules[mod]);
      const nextStep = allModulesAnswered ? 'results' : 'questions';
      
      return {
        ...state,
        modules: updatedModules,
        currentStep: nextStep,
      };

    case 'SET_CURRENT_STEP':
      return {
        ...state,
        currentStep: action.payload,
      };

    case 'RESET_ASSESSMENT':
      return initialState;

    case 'INITIALIZE_ASSESSMENT':
      return {
        ...initialState,
        assessment_id: crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substr(2, 9),
        created_at: new Date().toISOString(),
      };

    default:
      return state;
  }
}

interface AssessmentContextType {
  state: AssessmentState;
  dispatch: React.Dispatch<AssessmentAction>;
  getModuleScore: (module: string) => number;
  getTotalScore: () => number;
  getProgramSuggestions: () => Array<{ program: string; confidence: 'strong' | 'light' | 'none' }>;
}

const AssessmentContext = createContext<AssessmentContextType | undefined>(undefined);

export function AssessmentProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(assessmentReducer, initialState);

  const getModuleScore = (module: string): number => {
    const moduleData = state.modules[module];
    return moduleData && typeof moduleData.yes_count === 'number' ? moduleData.yes_count : 0;
  };

  const getTotalScore = (): number => {
    return typeof state.levelA.yes_count === 'number' ? state.levelA.yes_count : 0;
  };

  const getProgramSuggestions = (): Array<{ program: string; confidence: 'strong' | 'light' | 'none' }> => {
    const suggestions: Array<{ program: string; confidence: 'strong' | 'light' | 'none' }> = [];
    
    // Add suggestions based on Level A score
    if (state.levelA.yes_count >= 3) {
      suggestions.push({ program: 'general', confidence: 'strong' });
    } else if (state.levelA.yes_count >= 1) {
      suggestions.push({ program: 'general', confidence: 'light' });
    }

    // Add suggestions based on module scores
    Object.entries(state.modules).forEach(([module, data]) => {
      if (data.yes_count >= 3) {
        suggestions.push({ program: module, confidence: 'strong' });
      } else if (data.yes_count >= 1) {
        suggestions.push({ program: module, confidence: 'light' });
      }
    });

    // Sort by confidence (strong first, then light)
    return suggestions.sort((a, b) => {
      if (a.confidence === 'strong' && b.confidence !== 'strong') return -1;
      if (b.confidence === 'strong' && a.confidence !== 'strong') return 1;
      return 0;
    });
  };

  const value: AssessmentContextType = {
    state,
    dispatch,
    getModuleScore,
    getTotalScore,
    getProgramSuggestions,
  };

  return (
    <AssessmentContext.Provider value={value}>
      {children}
    </AssessmentContext.Provider>
  );
}

export function useAssessment() {
  const context = useContext(AssessmentContext);
  if (context === undefined) {
    throw new Error('useAssessment must be used within an AssessmentProvider');
  }
  return context;
}
